import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProposalProvider, useProposal } from './contexts/ProposalContext';
import { 
  getProposals, 
  categorizeProposals, 
  listenToUser, 
  listenToProposal, 
  deleteAccount, 
  getProposal, 
  listenToPublicUser,
  deletePastProposals 
} from './services/DataStore';
import ProposalView from './components/ProposalView';
import NewProposalForm from './components/NewProposalForm';
import { Proposal } from './models/Proposal';
import { CustomMeal } from './models/CustomMeal';
import ProposalList from './components/ProposalList';
import UsersRestaurantList from './components/UsersRestaurantList';
import CreateNewProposal from './components/CreateNewProposal';
import { DeepLinkHandler } from './utils/DeepLinkHandler';
import FriendsList from './components/FriendsList';
import EditProfile from './components/EditProfile';
import { debug } from './utils/debug';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { useDeepLink } from './hooks/useDeepLink';
import { BUILD_VERSION } from './version';

// Components
const TopBar = ({ showRestaurantList, setShowRestaurantList }) => {
  const { currentUser, publicUserData, userData, signIn: handleSignIn, signOut } = useAuth();
  const { setSelectedProposal } = useProposal();
  const [showMenu, setShowMenu] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChangePicture = () => {
    // TODO: Implement change picture functionality
    debug.log('Change picture clicked');
    setShowMenu(false);
  };

  const handleChangeName = () => {
    // TODO: Implement change name functionality
    debug.log('Change name clicked');
    setShowMenu(false);
  };

  const handleEditProfile = () => {
    setShowMenu(false);
    setShowEditProfile(true);
  };

  const handleFriends = () => {
    setShowMenu(false);
    setShowFriendsList(true);
  };

  const handleRestaurants = () => {
    setShowMenu(false);
    setShowRestaurantList(true);
  };

  const handleSignOut = async () => {
    try {
      debug.log('TopBar handleSignOut called');
      await signOut();
      setShowMenu(false);
      setSelectedProposal(null);
      navigate('/');
    } catch (error) {
      debug.error('Error signing out:', error);
    }
  };

  const handleAbout = () => {
    window.open('https://twentyies.com', '_blank');
    setShowMenu(false);
  };

  const handleLogoClick = () => {
    setSelectedProposal(null);
    navigate('/', { replace: true });
  };

  return (
    <div className="bg-beige shadow-md p-4 flex justify-between items-center h-[72px]">
      <div className="h-full flex items-center cursor-pointer" onClick={handleLogoClick}>
        <img 
          src="/images/logo_flat.png" 
          alt="DiDeMa" 
          className="h-full w-auto object-contain"
        />
      </div>
      <div className="flex items-center space-x-4">
        {currentUser ? (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center space-x-2 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors"
            >
              <div className="w-12 h-12 rounded-full border-2 border-accent overflow-hidden">
                {publicUserData?.avatarurl ? (
                  <img 
                    src={publicUserData.avatarurl} 
                    alt={publicUserData.displayname}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-500">account_circle</span>
                  </div>
                )}
              </div>
              <span className="text-accent font-bold">{publicUserData?.displayname || currentUser.displayName}</span>
              <span className="material-symbols-outlined text-accent">expand_more</span>
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                <button
                  onClick={handleEditProfile}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Edit Profile...
                </button>
                <button
                  onClick={handleFriends}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Friends...
                </button>
                <button
                  onClick={handleRestaurants}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Restaurants...
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Sign Out
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={handleAbout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  About Twentyies Inc...
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleSignIn}
            className="bg-accent text-white px-4 py-2 rounded hover:bg-opacity-90"
          >
            Sign In with Google
          </button>
        )}
      </div>

      {/* Restaurant List Modal */}
      {showRestaurantList && (
        <UsersRestaurantList onClose={() => setShowRestaurantList(false)} />
      )}

      {/* Friends List Modal */}
      {showFriendsList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh]">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Friends</h2>
              <button
                onClick={() => setShowFriendsList(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <FriendsList
              friendIds={userData?.getFriends() || []}
              onClose={() => setShowFriendsList(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditProfile onClose={() => setShowEditProfile(false)} />
      )}
    </div>
  );
};

const SideBar = ({ showCreateNewProposal, setShowCreateNewProposal, proposals }) => {
  const { userData } = useAuth();
  const { selectedProposal, setSelectedProposal, pendingProposalNavigation, setPendingProposalNavigation } = useProposal();
  const [showDeletePastConfirm, setShowDeletePastConfirm] = useState(false);
  const [showDeletePastSuccess, setShowDeletePastSuccess] = useState(false);
  const navigate = useNavigate();
  const userInitiatedSelectionRef = useRef(false);

  const handleProposalSelect = (proposal) => {
    debug.log('SideBar handleProposalSelect called with:', {
      proposalId: proposal?.id,
      state: proposal?.getStateEnum?.(),
      currentSelectedId: selectedProposal?.id,
      pendingNavigation: pendingProposalNavigation
    });
    
    // Set the user-initiated flag
    userInitiatedSelectionRef.current = true;
    
    // First set the selected proposal
    setSelectedProposal(proposal);
    // Then close the CreateNewProposal view
    setShowCreateNewProposal(false);
    // Navigate to the proposal URL
    navigate(`/proposal/${proposal.id}`, { replace: true });
  };

  // Don't show sidebar content if not signed in
  if (!userData) {
    return null;
  }

  const { future, recent, past } = categorizeProposals(proposals);

  const handleDeletePastEvents = async () => {
    try {
      await deletePastProposals();
      setShowDeletePastConfirm(false);
      setShowDeletePastSuccess(true);
    } catch (error) {
      debug.error('Error deleting past events:', error);
      alert('Failed to delete past events. Please try again.');
      setShowDeletePastConfirm(false);
    }
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <button
          onClick={() => {
            debug.log('SideBar new event button clicked');
            setSelectedProposal(null);
            setShowCreateNewProposal(true);
          }}
          className="w-full bg-accent text-white px-4 py-2 rounded hover:bg-opacity-90"
        >
          New Event
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {future.length > 0 && (
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-2">Upcoming</h2>
            <ProposalList
              proposals={future}
              selectedProposal={selectedProposal}
              onProposalSelect={handleProposalSelect}
              variant="compact"
              currentUser={userData}
            />
          </div>
        )}

        {recent.length > 0 && (
          <div className="p-4 border-t border-gray-200">
            <h2 className="text-lg font-semibold mb-2">Recent</h2>
            <ProposalList
              proposals={recent}
              selectedProposal={selectedProposal}
              onProposalSelect={handleProposalSelect}
              variant="compact"
              currentUser={userData}
            />
          </div>
        )}

        {past.length > 0 && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold">Past</h2>
              <button
                onClick={() => setShowDeletePastConfirm(true)}
                className="text-sm bg-accent text-white px-3 py-1 rounded hover:bg-opacity-90"
              >
                Delete Past Events
              </button>
            </div>
            <ProposalList
              proposals={past}
              selectedProposal={selectedProposal}
              onProposalSelect={handleProposalSelect}
              variant="compact"
              currentUser={userData}
            />
          </div>
        )}

        {future.length === 0 && recent.length === 0 && past.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No proposals found. Create a new proposal to get started!
          </div>
        )}
      </div>

      {/* Delete Past Events Confirmation Dialog */}
      {showDeletePastConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Past Events</h3>
            <p className="text-gray-600 mb-6">
              Do you want to remove yourself from events older than one week?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeletePastConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePastEvents}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Past Events Success Dialog */}
      {showDeletePastSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Success</h3>
            <p className="text-gray-600 mb-6">
              You have left all past events
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDeletePastSuccess(false)}
                className="px-4 py-2 bg-accent text-white rounded hover:bg-opacity-90"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MainContent = ({ showCreateNewProposal, setShowCreateNewProposal, onProposalUpdate }) => {
  const { selectedProposal, setSelectedProposal, pendingProposalNavigation, setPendingProposalNavigation } = useProposal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoadingProposal, setIsLoadingProposal] = useState(false);
  const [deepLinkProposalId, setDeepLinkProposalId] = useState(null);
  const { userData } = useAuth();
  const deepLinkAction = useDeepLink();
  const userInitiatedSelectionRef = useRef(false);
  
  debug.log('MainContent rendered with:', {
    pathname: location.pathname,
    showCreateNewProposal,
    selectedProposalId: selectedProposal?.id,
    selectedProposalState: selectedProposal?.getStateEnum?.(),
    pendingNavigation: pendingProposalNavigation,
    deepLinkAction,
    userInitiated: userInitiatedSelectionRef.current
  });

  // Handle deep link action
  useEffect(() => {
    if (deepLinkAction?.type === 'openproposal') {
      debug.log('Handling deep link action:', deepLinkAction);
      setDeepLinkProposalId(deepLinkAction.proposalId);
      
      if (userData) {
        // User is signed in, load the proposal
        setIsLoadingProposal(true);
        getProposal(deepLinkAction.proposalId).then(proposal => {
          if (proposal) {
            debug.log('Loaded proposal from deep link:', {
              id: proposal.id,
              state: proposal.getStateEnum()
            });
            setSelectedProposal(proposal);
            setPendingProposalNavigation(null);
            navigate(`/proposal/${proposal.id}`, { replace: true });
          } else {
            debug.error('Proposal not found from deep link:', deepLinkAction.proposalId);
            navigate('/');
          }
          setIsLoadingProposal(false);
        }).catch(error => {
          debug.error('Error loading proposal from deep link:', error);
          navigate('/');
          setIsLoadingProposal(false);
        });
      }
    }
  }, [deepLinkAction, userData, setSelectedProposal, setPendingProposalNavigation, navigate]);

  // Extract proposal ID from URL and load proposal
  useEffect(() => {
    const proposalIdFromUrl = location.pathname.split('/proposal/')[1];
    
    // Only load from URL if:
    // 1. There's a proposal ID in the URL AND
    // 2. Either there's no selected proposal OR
    // 3. The URL proposal ID is different from the current selection AND
    // 4. The selection wasn't user-initiated
    if (proposalIdFromUrl && 
        (!selectedProposal || 
         selectedProposal.id !== proposalIdFromUrl) && 
        !userInitiatedSelectionRef.current) {
      debug.log('Loading proposal from URL:', proposalIdFromUrl);
      setIsLoadingProposal(true);
      
      // Load the proposal directly
      getProposal(proposalIdFromUrl).then(proposal => {
        if (proposal) {
          debug.log('Loaded proposal from URL:', {
            id: proposal.id,
            state: proposal.getStateEnum()
          });
          setSelectedProposal(proposal);
          setPendingProposalNavigation(null);
        } else {
          debug.error('Proposal not found:', proposalIdFromUrl);
          navigate('/');
        }
        setIsLoadingProposal(false);
      }).catch(error => {
        debug.error('Error loading proposal:', error);
        navigate('/');
        setIsLoadingProposal(false);
      });
    } else if (location.pathname === '/') {
      // Clear selected proposal when navigating to home
      debug.log('Clearing selected proposal due to home navigation');
      setSelectedProposal(null);
      setPendingProposalNavigation(null);
    }
    
    // Reset the user-initiated flag after handling the URL change
    userInitiatedSelectionRef.current = false;
  }, [location.pathname, selectedProposal, setPendingProposalNavigation, setSelectedProposal, navigate]);

  // Handle transitions between CreateNewProposal and proposal selection
  useEffect(() => {
    debug.log('MainContent useEffect triggered:', {
      showCreateNewProposal,
      selectedProposalId: selectedProposal?.id
    });
    
    if (showCreateNewProposal) {
      debug.log('Clearing selected proposal due to showCreateNewProposal');
      // When showing CreateNewProposal, clear the selected proposal
      setSelectedProposal(null);
    }
  }, [showCreateNewProposal, setSelectedProposal]);

  // Update loading state when proposal is selected
  useEffect(() => {
    if (selectedProposal) {
      setIsLoadingProposal(false);
    }
  }, [selectedProposal]);

  // Handle proposal deletion
  useEffect(() => {
    if (selectedProposal && location.pathname === '/') {
      debug.log('Clearing selected proposal due to home navigation after deletion');
      setSelectedProposal(null);
    }
  }, [location.pathname, selectedProposal, setSelectedProposal]);

  if (showCreateNewProposal) {
    debug.log('Rendering CreateNewProposal view');
    return (
      <div className="flex-1 bg-proposal-bg">
        <CreateNewProposal 
          onClose={(createdProposal) => {
            debug.log('CreateNewProposal onClose called with:', {
              createdProposalId: createdProposal?.id
            });
            setShowCreateNewProposal(false);
            if (createdProposal) {
              setSelectedProposal(createdProposal);
              navigate(`/proposal/${createdProposal.id}`, { replace: true });
            }
          }} 
        />
      </div>
    );
  }
  
  if (!selectedProposal) {
    debug.log('Rendering welcome view - no proposal selected');
    return (
      <div className="flex-1 bg-background relative">
        <div className="max-w-2xl mx-auto p-8">
          <h2 className="text-2xl font-bold mb-4 text-accent">Welcome to DiDeMa</h2>
          {isLoadingProposal ? (
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
              <p className="text-accent">Loading proposal...</p>
            </div>
          ) : !userData ? (
            <div className="space-y-4">
              <p className="text-accent">
                {deepLinkProposalId ? 
                  "Sign up or sign in to view this shared event!" :
                  "Sign up or sign in to begin"
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-accent">
                Select an event from the sidebar or create a new one to get started.
              </p>
              <button
                onClick={() => setShowCreateNewProposal(true)}
                className="bg-accent text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors"
              >
                Create New Event
              </button>
            </div>
          )}
        </div>
        {/* Version display in bottom right */}
        <div className="absolute bottom-4 right-4 text-xs text-gray-500 bg-white bg-opacity-80 px-2 py-1 rounded">
          v{BUILD_VERSION}
        </div>
      </div>
    );
  }

  debug.log('Rendering ProposalView for proposal:', {
    id: selectedProposal.id,
    state: selectedProposal.getStateEnum?.()
  });
  
  return (
    <div className="flex-1 bg-proposal-bg">
      <ProposalView 
        proposal={selectedProposal} 
        onProposalUpdate={onProposalUpdate}
      />
    </div>
  );
};

const AppContent = () => {
  const [showRestaurantList, setShowRestaurantList] = useState(false);
  const [showCreateNewProposal, setShowCreateNewProposal] = useState(false);
  const { currentUser, userData, setUserData, setPublicUserData, signIn: handleSignIn } = useAuth();
  const { selectedProposal, setSelectedProposal } = useProposal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isHandlingDeepLink, setIsHandlingDeepLink] = useState(false);
  const signInAttemptedRef = useRef(false);
  const [proposals, setProposals] = useState([]);

  // Handle proposal updates
  const handleProposalUpdate = useCallback((updatedProposal) => {
    debug.log('Handling proposal update:', {
      id: updatedProposal.id,
      state: updatedProposal.getStateEnum(),
      meal: updatedProposal.meal
    });

    // Update the proposals list
    setProposals(prevProposals => {
      const newProposals = prevProposals.map(p => 
        p.id === updatedProposal.id ? updatedProposal : p
      );
      debug.log('Updated proposals list:', newProposals.map(p => ({
        id: p.id,
        state: p.getStateEnum(),
        meal: p.meal
      })));
      return newProposals;
    });

    // If this is the selected proposal, update it
    if (selectedProposal?.id === updatedProposal.id) {
      setSelectedProposal(updatedProposal);
    }
  }, [selectedProposal?.id, setSelectedProposal]);

  // Set up listeners for user documents
  useEffect(() => {
    let userUnsubscribe = null;
    let publicUserUnsubscribe = null;

    if (currentUser?.uid) {
      debug.log('Setting up user document listeners for:', currentUser.uid);
      
      // Listen to private user document
      userUnsubscribe = listenToUser(currentUser.uid, (updatedUserData) => {
        debug.log('Private user data updated:', updatedUserData);
        setUserData(updatedUserData);

        // Get proposals from the updated user data
        const proposalIds = updatedUserData.getJoinedProposals();
        debug.log('Fetching proposals:', proposalIds);
        
        // Fetch each proposal
        Promise.all(
          proposalIds.map(async (id) => {
            try {
              const proposal = await getProposal(id);
              debug.log('Fetched proposal:', { id, exists: !!proposal });
              return proposal;
            } catch (error) {
              debug.error('Error fetching proposal:', { id, error });
              return null;
            }
          })
        ).then(proposals => {
          const validProposals = proposals.filter(p => p !== null);
          debug.log('Updated proposals:', validProposals.map(p => ({ 
            id: p.id, 
            state: p.getStateEnum(),
            meal: p.meal 
          })));
          setProposals(validProposals);
        });
      });

      // Listen to public user document
      publicUserUnsubscribe = listenToPublicUser(currentUser.uid, (updatedPublicUser) => {
        debug.log('Public user data updated:', updatedPublicUser);
        setPublicUserData(updatedPublicUser);
      });
    }

    return () => {
      if (userUnsubscribe) {
        debug.log('Cleaning up private user document listener');
        userUnsubscribe();
      }
      if (publicUserUnsubscribe) {
        debug.log('Cleaning up public user document listener');
        publicUserUnsubscribe();
      }
    };
  }, [currentUser?.uid, setUserData, setPublicUserData]);

  // Set up listener for selected proposal
  useEffect(() => {
    let proposalUnsubscribe = null;

    if (selectedProposal?.id) {
      debug.log('Setting up proposal listener for:', selectedProposal.id);
      
      // Listen to proposal document
      proposalUnsubscribe = listenToProposal(selectedProposal.id, (updatedProposal) => {
        if (updatedProposal) {
          debug.log('Proposal data updated:', updatedProposal);
          handleProposalUpdate(updatedProposal);
        } else {
          debug.log('Proposal no longer exists, clearing selection');
          setSelectedProposal(null);
        }
      });
    }

    return () => {
      if (proposalUnsubscribe) {
        debug.log('Cleaning up proposal listener');
        proposalUnsubscribe();
      }
    };
  }, [selectedProposal?.id, handleProposalUpdate]);

  return (
    <div className="flex flex-col h-screen">
      <TopBar 
        showRestaurantList={showRestaurantList}
        setShowRestaurantList={setShowRestaurantList}
      />
      <div className="flex flex-1 overflow-hidden">
        <SideBar 
          showCreateNewProposal={showCreateNewProposal}
          setShowCreateNewProposal={setShowCreateNewProposal}
          proposals={proposals}
        />
        <Routes>
          <Route 
            path="/" 
            element={
              <MainContent 
                showCreateNewProposal={showCreateNewProposal}
                setShowCreateNewProposal={setShowCreateNewProposal}
                onProposalUpdate={handleProposalUpdate}
              />
            } 
          />
          <Route 
            path="/proposal/:id" 
            element={
              <MainContent 
                showCreateNewProposal={showCreateNewProposal}
                setShowCreateNewProposal={setShowCreateNewProposal}
                onProposalUpdate={handleProposalUpdate}
              />
            } 
          />
          <Route 
            path="/openproposal/action" 
            element={
              <MainContent 
                showCreateNewProposal={showCreateNewProposal}
                setShowCreateNewProposal={setShowCreateNewProposal}
                onProposalUpdate={handleProposalUpdate}
              />
            } 
          />
        </Routes>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <ProposalProvider>
          <GoogleMapsProvider>
            <AppContent />
          </GoogleMapsProvider>
        </ProposalProvider>
      </AuthProvider>
    </Router>
  );
};

export default App; 